import { BackEndValueType, EPropertyType, IDisplayItem, IProperty, PropertyToolType } from "@kuika/kuika-cl-model";
import { KMainFunctions } from "./main-functions";

/**
 * Properties edited as a CSS length: a number input paired with a unit select
 * (px / % / vw for horizontal, vh for vertical) instead of a free-text input.
 *
 * KOA-13797 added the Splitter pane sizes. They are lengths like the others, but they
 * are measured against the splitter rather than the viewport, so they offer no vw/vh —
 * see `getUnitInputOptions`.
 */
export type UnitInputPropertyName = "width" | "height" | "top" | "defaultSize" | "minSize" | "maxSize";

const UNIT_INPUT_PROPERTY_NAMES: readonly UnitInputPropertyName[] = [
  "width",
  "height",
  "top",
  "defaultSize",
  "minSize",
  "maxSize"
];

/** Units offered in the select, in display order. */
const VIEWPORT_LESS_UNITS: readonly string[] = ["px", "%"];

/**
 * The unit list for a length property. `width` is the only horizontal one, so it offers
 * `vw`; `height` and `top` offer `vh`. A Splitter pane gets neither: it is sized against
 * its splitter, not the viewport, and its axis comes from the parent's `orientation`,
 * which this editor cannot see from the property name alone.
 */
export const getUnitInputOptions = (propertyName: UnitInputPropertyName | undefined): readonly string[] => {
  if (propertyName === "width") {
    return ["px", "%", "vw"];
  }
  if (propertyName === "height" || propertyName === "top") {
    return ["px", "%", "vh"];
  }
  return VIEWPORT_LESS_UNITS;
};

export class PropertyHelper {
  /**
   * Returns the unit-input kind for a property name, or undefined when the property
   * is edited as plain text. "width" is the only horizontal one, so it offers vw;
   * "height" and "top" offer vh.
   */
  static getUnitInputPropertyName = (propertyName: string | undefined): UnitInputPropertyName | undefined => {
    if (propertyName === undefined) {
      return undefined;
    }
    return UNIT_INPUT_PROPERTY_NAMES.find((name: UnitInputPropertyName) => name === propertyName);
  };

  static getTextValueForDisplay = (value: string): string => {
    if (!value) {
      value = "";
    }
    if (value && value.replace) {
      value = value.replace(/&nbsp;/g, " ");
    }
    return value;
  };

  static getTextValueForDisplayWidthorHeight = (value: string, widthOrHeightValue: string): string => {
    if (!value) {
      value = "";
    }
    if (value && value.replace) {
      value = value.replace(widthOrHeightValue, "");
    }
    return value;
  };

  static getWidthOrHeightValueForDisplay = (value: string): string => {
    if (!value) {
      value = "";
    }
    if (value && value.replace) {
      // Strip the leading numeric magnitude (optional sign, digits, optional decimal part) to
      // leave only the trailing unit ("px" / "%" / "vw" / "vh"). Stripping digits alone (the
      // previous approach) left a dangling "." or "-" behind for decimal/negative values, e.g.
      // "46.5px" -> ".px" and "-10px" -> "-px", neither of which matched a real unit. A value
      // with no leading number (e.g. "auto") is left untouched and treated as the whole unit.
      value = value.replace(/^-?\d+(\.\d+)?/, "");
    }
    return value;
  };

  /**
   * Joins a unit input's number and unit into the value stored on the property. A cleared number
   * stores "" (unset) rather than the bare unit: the select always holds a unit, and the React
   * generator writes a non-blank value verbatim, so "px" alone would reach the app as width="px".
   */
  static composeUnitValue = (value: string | number | null | undefined, unit: string): string => {
    const magnitude: string = value === null || value === undefined ? "" : String(value).trim();
    return magnitude === "" ? "" : magnitude + unit;
  };

  static getTextValueForMetaData = (value: string, shouldTrim: boolean = false): string => {
    if (typeof value !== "string") return value;

    if (shouldTrim) {
      value = value?.trim();
    }
    if (value && value?.replace) {
      value = value.replace(/[\r\n\v]+/g, "&nbsp;");
      value = value.replace(/^\s|\s$/g, "&nbsp;");
    }
    return value;
  };

  calculatePropertyToolType = (di: IDisplayItem, p: IProperty): PropertyToolType => {
    // KOA-18129: the Date's specific-days list is edited with the options editor, but only the editor is
    // shared — it never becomes an options prop.
    if (this.isOptionProperty(p) || this.isSpecificDatesProperty(p)) {
      return "Options";
    }
    if (this.isCalendarOptionsProp(p)) {
      return "CalendarOptions";
    }
    if (this.isMenuOptionsProp(p)) {
      return "MenuOptions";
    }
    if (this.isBreadcrumbOptionsProp(p)) {
      return "BreadcrumbOptions";
    }
    if (this.isRadialChartOptionsProp(p) === true) {
      return "RadialChartOptions";
    }
    if (this.isRadialChartLevelOptionsProp(p) === true) {
      return "RadialChartLevelOptions";
    }
    if (this.isMixedChartOptionsProp(p) === true) {
      return "MixedChartOptions";
    }
    if (this.isMixedChartLineOptionsProp(p) === true) {
      return "MixedChartLineOptions";
    }
    if (this.isMixedChartPointOptionsProp(p) === true) {
      return "MixedChartPointOptions";
    }
    if (this.isMixedChartBarOptionsProp(p) === true) {
      return "MixedChartBarOptions";
    }
    if (this.isTableOptionProperty(p) === true) {
      return "TableOptions";
    }
    if (this.isGoogleMapsOptionProperty(p) === true) {
      return "GoogleMapsOptions";
    }
    if (this.isKanbanBoardLaneOptionProperty(p) === true) {
      return "KanbanBoardLaneOptions";
    }
    if (this.isKanbanBoardCardOptionProperty(p) === true) {
      return "KanbanBoardCardOptions";
    }
    if (this.isNodeTreeNodeOptionProperty(p) === true) {
      return "NodeTreeNodeOptions";
    }
    if (this.isDirectoryOptionsProperty(p) === true) {
      return "DirectoryOptions";
    }
    if (this.isTagsInputProperty(p) === true) {
      return "Tags";
    }
    if (this.isSelectProperty(p) === true) {
      return "Select";
    }
    if (this.isResponsiveColInput(p) === true) {
      return "ResponsiveColumn";
    }
    if (this.isEnumProperty(p) === true) {
      return "Enum";
    }
    if (p.name === "style") {
      return "Style";
    }
    // Widget domain type properties: arrays use data source picker, single objects use symbol picker
    if (this.isWidgetDomainTypeProperty(p)) {
      const isList = p.isList ?? p.dynamicPropertyDefinition?.isList;
      return isList ? "WidgetDomainType" : "WidgetDomainTypeObject";
    }
    // Primitive arrays (e.g., number[], string[]) — show symbol picker for datasource binding, not JSONEditor
    if (this.isWidgetPrimitiveArrayProperty(p)) {
      return "String";
    }
    if (this.isItObject(p) === true) {
      return "JSONEditor";
    }
    if (this.isInputNumber(p) === true) {
      return "Number";
    }
    if (this.isSwitchProperty(p) === true) {
      return "Boolean";
    }
    if (this.isImageProperty(p) === true) {
      return "Image";
    }
    if (this.isIconProperty(p) === true) {
      return "Icon";
    }
    if (this.isColorPickerArrayProperty(p) === true) {
      return "ColorPickerArray";
    }
    if (this.isColorPickerProperty(p) === true) {
      return "ColorPicker";
    }
    if (this.isDateTimeProperty(p) === true) {
      return "DateTime";
    }
    if (this.isChartOptionsProperty(p) === true) {
      return "ChartOptions";
    }
    if (this.isTimelineOptionsProperty(p) === true) {
      return "TimelineOptions";
    }
    if (this.isCalendarOptionsProp(p) === true) {
      return "CalendarOptions";
    }
    if (this.isMenuOptionsProp(p) === true) {
      return "MenuOptions";
    }
    if (this.isBreadcrumbOptionsProp(p) === true) {
      return "BreadcrumbOptions";
    }
    if (this.isRadialChartOptionsProp(p) === true) {
      return "RadialChartOptions";
    }
    if (this.isRadialChartLevelOptionsProp(p) === true) {
      return "RadialChartLevelOptions";
    }
    if (this.isMixedChartOptionsProp(p) === true) {
      return "MixedChartOptions";
    }
    if (this.isMixedChartLineOptionsProp(p) === true) {
      return "MixedChartLineOptions";
    }
    if (this.isMixedChartPointOptionsProp(p) === true) {
      return "MixedChartPointOptions";
    }
    if (this.isMixedChartBarOptionsProp(p) === true) {
      return "MixedChartBarOptions";
    }
    if (this.isTreeOptionsProp(p) === true) {
      return "TreeOptions";
    }
    if (this.isChatOptionsProp(p) === true) {
      return "ChatOptions";
    }
    if (this.isNodeTreeNodeOptionProperty(p) === true) {
      return "NodeTreeNodeOptions";
    }
    if (this.isDirectoryOptionsProperty(p) === true) {
      return "DirectoryOptions";
    }
    if (this.isGoogleMapMarkerProp(p) === true) {
      return "MapMarkerProp";
    }
    if (this.isMicroAppProjectProp(di, p) === true) {
      return "MicroAppProjectProp";
    }
    if (this.isMicroAppStartingScreenProp(di, p) === true) {
      return "MicroAppStartingScreenProp";
    }
    if (p.name === "dynamicformatter") {
      return "DynamicFormatter";
    }
    if (this.isDynamicFormEntityProp(di, p) === true) {
      return "DynamicFormEntity";
    }
    if (this.isDynamicFormAttributesProp(di, p) === true) {
      return "DynamicFormAttributes";
    }
    if (this.isDynamicFormValueProp(di, p) === true) {
      return "DynamicFormValue";
    }
    return "String";
  };

  calculateBackEndValueType = (p: IProperty): BackEndValueType => {
    if (p.kuikaTypeName) {
      return p.kuikaTypeName as BackEndValueType;
    }
    if (this.isInputNumber(p) === true) {
      return "Int64";
    }
    if (this.isSwitchProperty(p) === true) {
      return "Boolean";
    }
    if (this.isImageProperty(p) === true) {
      return "Image";
    }
    if (this.isDateTimeProperty(p) === true) {
      return "DateTimeOffset";
    }
    return "String";
  };

  isPropertyValueValid(value) {
    if (value === "" || value === undefined || value === null || value === "{}" || value === "[]") {
      return false;
    }
    return true;
  }

  hasBasicProperties = (properties: IProperty[]) => {
    return properties.filter((x) => x.isAdvanced !== true).length > 0;
  };

  hasAdvancedProperties = (properties: IProperty[]) => {
    return properties.filter((x) => x.isAdvanced === true).length > 0;
  };

  clearInput = (id) => {
    const input = document.getElementById(`input_property_${id}`) as HTMLInputElement;
    input.value = "";
  };

  willItUpdateColAttr = (property, event, gridSystemCount: number): string => {
    let newVal = "";

    if (this.isItResponsiveColItem(property)) {
      if (Number(event.target.value) < 0) {
        newVal = "0";
      } else if (Number(event.target.value) > gridSystemCount) {
        newVal = gridSystemCount.toString();
      }
    }
    if (newVal !== "") {
      event.target.value = newVal;
    } else {
      newVal = event.target.value;
    }
    return newVal;
  };

  getTextValueForMetaData = (property: IProperty, value): string => {
    const propertyType = Number(property.type);
    if (propertyType === EPropertyType.string) {
      if (value && value.replace) {
        value = value.replace(/^\s|\s$/g, "&nbsp;");
      }
    }
    return value;
  };

  getTextValueForDisplay = (property: IProperty, value): string => {
    const propertyType = Number(property.type);
    if (propertyType === EPropertyType.string) {
      if (!value) {
        value = "";
      }
      if (value && value.replace) {
        value = value.replace(/&nbsp;/g, " ");
      }
    }
    return value;
  };

  isInputPropery = (property: IProperty) => {
    if (this.needSelectPropertyType(property) && !property.selectedType) return false;
    return (
      !this.isSwitchProperty(property) &&
      !this.isSelectProperty(property) &&
      !this.isResponsiveColInput(property) &&
      !this.isEnumProperty(property)
    );
  };

  isSwitchProperty = (property: IProperty) => {
    if (this.needSelectPropertyType(property) && !property.selectedType) {
      return false;
    }
    if (property.selectedType === EPropertyType.boolean || property.selectedType === EPropertyType.onlyAttributeName) {
      return true;
    }
    const hasTrueOption = property?.options?.find((x) => x.toString().toLowerCase() === "true") !== undefined;
    const hasFalseOption = property?.options?.find((x) => x.toString().toLowerCase() === "false") !== undefined;

    if (property?.options?.length === 2 && hasTrueOption && hasFalseOption) {
      return true;
    }
    return false;
  };

  isTagsInputProperty = (property: IProperty) => {
    return property?.isTagsInput === true;
  };

  isSelectProperty = (property: IProperty) => {
    if (this.needSelectPropertyType(property) && !property.selectedType) return false;
    if (this.isSwitchProperty(property)) return false;

    if (property?.options?.length > 0) {
      return true;
    }

    return false;
  };

  isImageProperty = (property: IProperty) => {
    if (property.selectedType === EPropertyType.image) {
      return true;
    }
    return false;
  };

  isIconProperty = (property: IProperty) => {
    if (property.selectedType === EPropertyType.icon) {
      return true;
    }
    return false;
  };

  isResponsiveColInput = (property: IProperty) => {
    if (this.needSelectPropertyType(property) && !property.selectedType) return false;
    if (this.isItResponsiveColItem(property)) {
      return true;
    }
    return false;
  };

  isInputNumber = (property: IProperty) => {
    if (property.selectedType && property.selectedType === EPropertyType.number) {
      return true;
    }
    return false;
  };

  isOptionProperty = (property: IProperty) => {
    if (property && property.isOptionsProp && property.isOptionsProp === true) {
      return true;
    }
    return false;
  };

  isLanguageOptionsProperty = (property: IProperty) => {
    if (property && property.isLanguageOptionsProp && property.isLanguageOptionsProp === true) {
      return true;
    }
    return false;
  };

  isTableOptionProperty = (property: IProperty) => {
    if (property && property.isTableOptionsProp && property.isTableOptionsProp === true) {
      return true;
    }
    return false;
  };

  isGoogleMapsOptionProperty = (property: IProperty) => {
    if (property && property.isGoogleMapsOptionsProp && property.isGoogleMapsOptionsProp === true) {
      return true;
    }
    return false;
  };

  isKanbanBoardLaneOptionProperty = (property: IProperty) => {
    if (property && property.isKanbanBoardLaneOptionsProp && property.isKanbanBoardLaneOptionsProp === true) {
      return true;
    }
    return false;
  };

  isKanbanBoardCardOptionProperty = (property: IProperty) => {
    if (property && property.isKanbanBoardCardOptionsProp && property.isKanbanBoardCardOptionsProp === true) {
      return true;
    }
    return false;
  };

  isNodeTreeNodeOptionProperty = (property: IProperty) => {
    if (property && property.isNodeTreeNodeOptionsProp && property.isNodeTreeNodeOptionsProp === true) {
      return true;
    }
    return false;
  };

  isDirectoryOptionsProperty = (property: IProperty) => {
    if (property && property.isDirectoryOptionsProp && property.isDirectoryOptionsProp === true) {
      return true;
    }
    return false;
  };

  isSpecificDatesProperty = (property: IProperty): boolean => property?.isSpecificDatesProp === true;

  isDIHasResponsiveColProperty = (properties: IProperty[]): boolean => {
    if (properties === undefined) return false;
    let res = false;
    properties.forEach((x) => {
      if (this.isItResponsiveColItem(x)) {
        res = true;
      }
    });
    return res;
  };

  isItResponsiveColItem = (property: IProperty): boolean => {
    const colProperties = ["xs", "sm", "md", "lg", "xl", "xxl"];
    if (colProperties.indexOf(property.name.toLowerCase()) > -1 || property.selectedType === EPropertyType.column) {
      return true;
    }
    return false;
  };

  isEnumProperty = (property: IProperty) => {
    if (property.selectedType > -1 && property.selectedType === EPropertyType.enum) {
      return true;
    }
    if (!((property.type?.length ?? 0) > 1) && property.selectedType === EPropertyType.enum) {
      return true;
    }
    return false;
  };

  isItObject = (property: IProperty) => {
    if (property.selectedType > -1 && property.selectedType === EPropertyType.object) {
      return true;
    }
    if (!((property.type?.length ?? 0) > 1) && property.selectedType === EPropertyType.object) {
      return true;
    }
    return false;
  };

  /**
   * Checks if the property is a widget dynamic property with a domain type.
   * These properties should use WidgetDomainType to show the data source picker for field selection.
   *
   * Note: The property may have isDomainType/domainTypeName either:
   * - Directly on the property (from getPropertiesByDisplayItem construction)
   * - Or on dynamicPropertyDefinition (from raw displayItemProperty)
   */
  isWidgetDomainTypeProperty = (property: IProperty): boolean => {
    // Check both patterns - direct on property or via dynamicPropertyDefinition
    const isDomainType = property.isDomainType || property.dynamicPropertyDefinition?.isDomainType;
    const domainTypeName = property.domainTypeName || property.dynamicPropertyDefinition?.domainTypeName;
    return !!(isDomainType && domainTypeName);
  };

  /**
   * Checks if the property is a widget primitive array (e.g., number[], string[], boolean[]).
   * These have isList=true, selectedType=Object (from backend), but no domain type.
   */
  isWidgetPrimitiveArrayProperty = (property: IProperty): boolean => {
    const isList = property.isList ?? property.dynamicPropertyDefinition?.isList;
    if (!isList) return false;
    const isDomainType = property.isDomainType || property.dynamicPropertyDefinition?.isDomainType;
    if (isDomainType) return false;
    return property.selectedType === EPropertyType.object;
  };

  needSelectPropertyType = (property: IProperty) => {
    if ((property.type?.length ?? 0) > 1) {
      return true;
    }
    return false;
  };

  getEnumPropertyDefaultValue = (p: IProperty) => {
    if (p && p.typeOptions && KMainFunctions.isObjectEmpty(p.typeOptions) === false && p.typeOptions.find) {
      const pType = p.typeOptions.find((x) => x.propertyType === EPropertyType.enum.toString());
      if (pType) {
        const enums = pType.enums?.find((x) => x.value === p.symbol.fixedValue);
        if (enums) {
          return enums.key;
        }
      }
    }
    return p.symbol.fixedValue;
  };

  getPropertyTypeNamesFromProperty = (property: IProperty) => {
    const typesArr: any[] = [];
    if (property && property.type && property.type.length > 1) {
      property.type.forEach((pT) => {
        const propertyType = KMainFunctions.convertCamelCaseToFriendlyName(EPropertyType[pT]);
        typesArr.push({
          name: propertyType,
          value: pT
        });
      });
    }
    return typesArr;
  };

  getPropertyNameOld = (propertyName) => {
    if (propertyName.toLowerCase() === "children") {
      return "text";
    }
    return propertyName;
  };

  getPropertyName = (property: IProperty) => {
    let name = property?.tagName && property.tagName?.trim().length > 1 ? property.tagName : property.name;

    if (name.toLowerCase() === "children") {
      name = "text";
    }
    return name;
  };

  isColorPickerArrayProperty = (property: IProperty) => {
    if (property.selectedType === EPropertyType.colorPickerArray) {
      return true;
    }
    return false;
  };

  isColorPickerProperty = (property: IProperty) => {
    if (property.selectedType === EPropertyType.colorPicker) {
      return true;
    }
    return false;
  };

  isDateTimeProperty = (property: IProperty) => {
    if (property.selectedType === EPropertyType.dateTime) {
      return true;
    }
    return false;
  };

  isChartOptionsProperty = (property: IProperty) => {
    if (property && property.isChartOptionsProp && property.isChartOptionsProp === true) {
      return true;
    }
    return false;
  };

  isTimelineOptionsProperty = (property: IProperty) => {
    if (property && property.isTimelineOptionsProp && property.isTimelineOptionsProp === true) {
      return true;
    }
    return false;
  };

  isCalendarOptionsProp = (property: IProperty) => {
    if (property && property.isCalendarOptionsProp && property.isCalendarOptionsProp === true) {
      return true;
    }
    return false;
  };

  isMenuOptionsProp = (property: IProperty) => {
    if (property && property.isMenuOptionsProp && property.isMenuOptionsProp === true) {
      return true;
    }
    return false;
  };

  isBreadcrumbOptionsProp = (property: IProperty) => {
    if (property && property.isBreadcrumbOptionsProp && property.isBreadcrumbOptionsProp === true) {
      return true;
    }
    return false;
  };

  isRadialChartOptionsProp = (property: IProperty) => {
    if (property && property.isRadialChartOptionsProp && property.isRadialChartOptionsProp === true) {
      return true;
    }
    return false;
  };

  isRadialChartLevelOptionsProp = (property: IProperty) => {
    if (property && property.isRadialChartLevelOptionsProp && property.isRadialChartLevelOptionsProp === true) {
      return true;
    }
    return false;
  };

  isMixedChartOptionsProp = (property: IProperty) => {
    if (property && property.isMixedChartOptionsProp && property.isMixedChartOptionsProp === true) {
      return true;
    }
    return false;
  };

  isMixedChartLineOptionsProp = (property: IProperty) => {
    if (property && property.isMixedChartLineOptionsProp && property.isMixedChartLineOptionsProp === true) {
      return true;
    }
    return false;
  };

  isMixedChartPointOptionsProp = (property: IProperty) => {
    if (property && property.isMixedChartPointOptionsProp && property.isMixedChartPointOptionsProp === true) {
      return true;
    }
    return false;
  };

  isMixedChartBarOptionsProp = (property: IProperty) => {
    if (property && property.isMixedChartBarOptionsProp && property.isMixedChartBarOptionsProp === true) {
      return true;
    }
    return false;
  };

  isChatOptionsProp = (property: IProperty) => {
    if (property && property.isChatOptionsProp && property.isChatOptionsProp === true) {
      return true;
    }
    return false;
  };

  isTreeOptionsProp = (property: IProperty) => {
    if (property && property.isTreeOptionsProp && property.isTreeOptionsProp === true) {
      return true;
    }
    return false;
  };

  isGoogleMapMarkerProp = (property: IProperty) => {
    if (property && property.name == "markers") {
      return true;
    }
    return false;
  };

  isMicroAppProjectProp = (di: IDisplayItem, property: IProperty): boolean => {
    if (di.item.name !== "MicroApp") return false;
    return property?.name === "projectId";
  };

  isMicroAppStartingScreenProp = (di: IDisplayItem, property: IProperty): boolean => {
    if (di.item.name !== "MicroApp") return false;
    return property?.name === "startingScreen";
  };

  isDynamicFormEntityProp = (di: IDisplayItem, property: IProperty): boolean => {
    if (di.item.name !== "DynamicForm") return false;
    return property?.name?.toLowerCase() === "entity";
  };

  isDynamicFormAttributesProp = (di: IDisplayItem, property: IProperty): boolean => {
    if (di.item.name !== "DynamicForm") return false;
    return property?.name?.toLowerCase() === "attributes";
  };

  isDynamicFormValueProp = (di: IDisplayItem, property: IProperty): boolean => {
    if (di.item.name !== "DynamicForm") return false;
    return property?.name?.toLowerCase() === "value";
  };
}
